/**
 * Canvas types for JSON Canvas Components feature.
 * See: specs/017-json-canvas/data-model.md
 *
 * Component-based model:
 * - Canvas = named container (e.g., "main", "sidebar")
 * - CanvasComponent = individual element with unique componentId within a canvas
 * - Updates target specific components by ID, not replacing the whole canvas
 */

// ============================================================================
// Canvas Status and Core Types
// ============================================================================

export type CanvasStatus = 'idle' | 'streaming' | 'complete' | 'error';

/**
 * Actions that can be performed on a canvas component.
 */
export type CanvasAction = 'upsert' | 'remove';

/**
 * Canvas ID validation pattern: starts with letter, max 64 chars.
 * Pattern: ^[a-zA-Z][a-zA-Z0-9-_]{0,63}$
 */
export const CANVAS_ID_PATTERN = /^[a-zA-Z][a-zA-Z0-9-_]{0,63}$/;

/**
 * Maximum number of canvases per session.
 */
export const MAX_CANVASES = 5;

/**
 * Maximum number of components per canvas.
 */
export const MAX_COMPONENTS_PER_CANVAS = 20;

/**
 * Maximum nesting depth for component trees.
 */
export const MAX_NESTING_DEPTH = 10;

/**
 * Maximum payload size in bytes (1MB).
 */
export const MAX_PAYLOAD_SIZE = 1024 * 1024;

// ============================================================================
// Visibility Conditions
// ============================================================================

export type VisibilityCondition =
  | boolean
  | { path: string; equals?: unknown }
  | { and: VisibilityCondition[] }
  | { or: VisibilityCondition[] }
  | { not: VisibilityCondition };

// ============================================================================
// Component Tree
// ============================================================================

/**
 * Supported component types in the catalog.
 */
export type ComponentType =
  | 'Card'
  | 'Text'
  | 'Table'
  | 'Chart'
  | 'Form'
  | 'Button'
  | 'Alert'
  | 'Image';

/**
 * JSON structure defining UI components to render.
 */
export interface ComponentTree {
  type: ComponentType | string; // Allow string for forward compatibility
  props: Record<string, unknown>;
  children?: ComponentTree[];
  visible?: VisibilityCondition;
}

// ============================================================================
// Canvas Component (individual element within a canvas)
// ============================================================================

/**
 * An individual component within a canvas.
 */
export interface CanvasComponent {
  id: string; // componentId
  artifactId: string;
  jsonTree: ComponentTree;
  status: CanvasStatus;
  error?: string;
  progress?: number; // 0.0-1.0 streaming progress
  updatedAt: number;
  createdAt: number;
}

// ============================================================================
// Canvas Entity
// ============================================================================

/**
 * A display region that renders multiple components.
 */
export interface Canvas {
  id: string;
  components: Map<string, CanvasComponent>; // componentId -> CanvasComponent
  status: CanvasStatus;
  error?: string;
  progress?: number; // 0.0-1.0 streaming progress (from most recent update)
  updatedAt: number;
  createdAt: number;
}

// ============================================================================
// Canvas State (Frontend)
// ============================================================================

/**
 * Frontend state container for all canvases.
 */
export interface CanvasState {
  canvases: Map<string, Canvas>;
  activeCanvasId: string | null;
  activeComponentId: string | null;
  panelVisible: boolean;
  panelWidth: number;
}

/**
 * Initial canvas state.
 */
export const INITIAL_CANVAS_STATE: CanvasState = {
  canvases: new Map(),
  activeCanvasId: null,
  activeComponentId: null,
  panelVisible: false,
  panelWidth: 400,
};

// ============================================================================
// Canvas Actions (Frontend -> Backend)
// ============================================================================

/**
 * Action payload for interactive components.
 */
export interface ActionPayload {
  name: string;
  params?: Record<string, unknown>;
}

/**
 * User-initiated action from interactive component.
 */
export interface CanvasUserAction {
  canvasId: string;
  componentId?: string;
  artifactId: string;
  action: ActionPayload;
}

/**
 * Action definition with optional confirmation.
 */
export interface ActionDefinition {
  name: string;
  params?: Record<string, unknown>;
  confirm?: {
    title: string;
    message: string;
  };
}

// ============================================================================
// Canvas Events (Backend -> Frontend)
// ============================================================================

/**
 * Data payload for canvas update events.
 */
export interface CanvasUpdateData {
  canvasId: string;
  componentId?: string;
  artifactId: string;
  jsonTree?: ComponentTree;
  action?: CanvasAction;
  progress?: number;
}

/**
 * Data payload for canvas complete events.
 */
export interface CanvasCompleteData {
  canvasId: string;
  componentId?: string;
  artifactId: string;
  jsonTree?: ComponentTree;
  action?: CanvasAction;
}

/**
 * Data payload for canvas error events.
 */
export interface CanvasErrorData {
  canvasId: string;
  componentId?: string;
  artifactId: string;
  error: string;
}

// ============================================================================
// Component Catalog Types
// ============================================================================

/**
 * Catalog entry metadata.
 */
export interface CatalogEntry {
  name: ComponentType;
  hasChildren: boolean;
  description: string;
}

/**
 * Component catalog metadata (without React components - those are in catalog.ts).
 */
export const CATALOG_METADATA: Record<ComponentType, CatalogEntry> = {
  Card: {
    name: 'Card',
    hasChildren: true,
    description: 'Container with optional title and subtitle',
  },
  Text: {
    name: 'Text',
    hasChildren: false,
    description: 'Text display with optional markdown support',
  },
  Table: {
    name: 'Table',
    hasChildren: false,
    description: 'Data table with columns and rows',
  },
  Chart: {
    name: 'Chart',
    hasChildren: false,
    description: 'Data visualization (line, bar, pie, area)',
  },
  Form: {
    name: 'Form',
    hasChildren: true,
    description: 'Interactive form with fields',
  },
  Button: {
    name: 'Button',
    hasChildren: false,
    description: 'Clickable action trigger',
  },
  Alert: {
    name: 'Alert',
    hasChildren: false,
    description: 'Status or warning message',
  },
  Image: {
    name: 'Image',
    hasChildren: false,
    description: 'Image display with alt text',
  },
};

// ============================================================================
// Component Props Types
// ============================================================================

export interface CardProps {
  title?: string;
  subtitle?: string;
  variant?: 'default' | 'outlined' | 'elevated';
}

export interface TextProps {
  content: string;
  variant?: 'body' | 'heading' | 'caption' | 'code';
  format?: 'plain' | 'markdown';
}

export interface TableColumn {
  key: string;
  header: string;
  width?: string;
  align?: 'left' | 'center' | 'right';
}

export interface TableProps {
  columns: TableColumn[];
  rows: Record<string, unknown>[];
  sortable?: boolean;
  pageSize?: number;
}

export type ChartType = 'line' | 'bar' | 'pie' | 'area';

export interface ChartProps {
  type: ChartType;
  data: Record<string, unknown>[];
  xKey: string;
  yKey: string;
  title?: string;
  xLabel?: string;
  yLabel?: string;
  colors?: string[];
}

export type FormFieldType =
  | 'text'
  | 'email'
  | 'password'
  | 'number'
  | 'textarea'
  | 'select'
  | 'checkbox';

export interface FormFieldOption {
  value: string;
  label: string;
}

export interface FormField {
  name: string;
  type: FormFieldType;
  label: string;
  placeholder?: string;
  required?: boolean;
  defaultValue?: unknown;
  options?: FormFieldOption[];
}

export interface FormProps {
  fields: FormField[];
  submitLabel?: string;
  onSubmit?: { action: string };
}

export type ButtonVariant = 'primary' | 'secondary' | 'danger' | 'ghost';

export interface ButtonProps {
  label: string;
  action: ActionDefinition;
  variant?: ButtonVariant;
  disabled?: boolean;
  loading?: boolean;
}

export type AlertSeverity = 'info' | 'success' | 'warning' | 'error';

export interface AlertProps {
  message: string;
  severity?: AlertSeverity;
  title?: string;
  dismissible?: boolean;
}

export type ImageObjectFit = 'contain' | 'cover' | 'fill' | 'none';

export interface ImageProps {
  src: string;
  alt: string;
  width?: string;
  height?: string;
  objectFit?: ImageObjectFit;
}

// ============================================================================
// Validation Result
// ============================================================================

export interface ValidationResult {
  valid: boolean;
  errors: string[];
}

// ============================================================================
// Canvas Callbacks
// ============================================================================

export interface CanvasCallbacks {
  onCanvasUpdate?: (data: CanvasUpdateData) => void;
  onCanvasComplete?: (data: CanvasCompleteData) => void;
  onCanvasError?: (data: CanvasErrorData) => void;
}
